export interface Credential {
  salt: string
  hash: string
}

function toHex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
}

/** Laver et tilfældigt salt, så to identiske adgangskoder ikke får samme hash. */
export function randomSalt(): string {
  const bytes = new Uint8Array(16)
  if (typeof crypto !== 'undefined' && 'getRandomValues' in crypto) {
    crypto.getRandomValues(bytes)
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256)
  }
  return toHex(bytes.buffer)
}

/**
 * Hasher en adgangskode med SHA-256 og et salt.
 * Adgangskoden gemmes aldrig i klartekst — kun hash og salt lagres.
 */
export async function hashPassword(password: string, salt: string): Promise<string> {
  const data = new TextEncoder().encode(`${salt}:${password}`)
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const digest = await crypto.subtle.digest('SHA-256', data)
    return toHex(digest)
  }
  // Nødløsning hvis Web Crypto ikke er tilgængelig (fx meget gamle browsere).
  let hash = 0
  for (let i = 0; i < data.length; i += 1) {
    hash = (hash << 5) - hash + data[i]
    hash |= 0
  }
  return `fallback-${Math.abs(hash).toString(16)}`
}

export async function makeCredential(password: string): Promise<Credential> {
  const salt = randomSalt()
  return { salt, hash: await hashPassword(password, salt) }
}

export async function verifyCredential(password: string, credential: Credential): Promise<boolean> {
  const hash = await hashPassword(password, credential.salt)
  return hash === credential.hash
}
